import { PersonLabelContext, UserStatus } from '@bp/bp-graphql-types';
import { GroupRoles } from '@bp/pim-auth-constants';
import { Grid, GridRow, InfoIcon, Modal, ModalBottomButtons, Tooltip } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { PersonsLabelFilter } from 'components/Persons/components/PersonsLabelFilter/PersonsLabelFilter';
import { PersonsList } from 'components/Persons/components/PersonsList/PersonsList';
import { PersonLabelSource, PersonLabelType, PersonListType } from 'components/Persons/types/personTypes';
import { labelIsInLabels } from 'components/Persons/utils/personUtils';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import styles from './CourseMembersModal.module.scss';

const UpdateGroupsMutationDocument = graphql(`
  mutation BpUpdateGroupsInCourseMemberModal($update: BpGroupUpdateInput!, $uuid: ID!) {
    updateBpGroup(update: $update, uuid: $uuid) {
      __typename
      uuid
      admins {
        __typename
        uuid
      }
      editors {
        __typename
        uuid
      }
      viewers {
        __typename
        uuid
      }
    }
  }
`);

const CourseMembersModalQuery = graphql(`
  query CourseMembersModalData(
    $courseUuid: ID!
    $organizationUuid: ID!
    $labelUuids: [ID!]!
    $schoolyearUuid: ID!
    $labelContext: PersonLabelContext!
  ) {
    courses: groups(where: { uuid: $courseUuid, type: course }) {
      __typename
      uuid
      admins {
        __typename
        uuid
        organizationRoles
      }
      editors {
        __typename
        uuid
        organizationRoles
      }
      viewers {
        __typename
        uuid
        organizationRoles
      }
    }
    persons: profiles(where: { organization: { uuid: $organizationUuid } }, options: { sort: [{ listName: ASC }] }) {
      __typename
      uuid
      updatedAt
      firstName
      lastName
      listName
      email
      birthday
      user {
        __typename
        uuid
        loginEmail
        status
      }
    }
    ...PersonsLabelFilterFields
    ...PersonsListFields
  }
`);

type CourseMemberType = {
  uuid: string;
  groupRole: GroupRoles;
};

type CourseMembersModalProps = {
  courseUuid: string;
  role: GroupRoles;
  isOpen: boolean;
  onClose: () => void;
};

export const CourseMembersModal = ({ courseUuid, role, isOpen, onClose }: CourseMembersModalProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const [, bpUpdateGroup] = useMutation(UpdateGroupsMutationDocument);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [{ data }] = useQuery({
    query: CourseMembersModalQuery,
    variables: {
      courseUuid,
      organizationUuid,
      labelUuids: [organizationUuid],
      schoolyearUuid: currentSchoolYear.uuid,
      labelContext: PersonLabelContext.Organization,
    },
  });

  const course = data?.courses[0] ?? null;

  const teachers: CourseMemberType[] =
    course?.editors.map((e) => ({
      uuid: e.uuid,
      groupRole: GroupRoles.Editor,
    })) ?? [];

  const students: CourseMemberType[] =
    course?.viewers.map((v) => ({
      uuid: v.uuid,
      groupRole: GroupRoles.Viewer,
    })) ?? [];

  const members: CourseMemberType[] = useMemo(() => [...teachers, ...students], [teachers, students]);

  const persons: PersonListType[] = useMemo(
    () =>
      data?.persons
        .filter((p) => !members.map((m) => m.uuid).includes(p.uuid))
        .map((p) => ({
          uuid: p.uuid,
          firstName: p.firstName ?? null,
          lastName: p.lastName ?? null,
          listName: p.listName ?? null,
          email: p.user?.loginEmail ?? null,
          birthday: p.birthday ?? null,
          userStatus: p.user?.status ?? UserStatus.None,
          updatedAt: p.updatedAt,
        })) ?? [],
    [data?.persons, members],
  );

  // filter and search
  const [activeSources, setActiveSources] = useState<PersonLabelSource[]>([
    'AddOn',
    'Class',
    'ClassGroup',
    'ProfileRole',
  ]);
  const [filterMode, setFilterMode] = useState<Record<PersonLabelSource, 'and' | 'or'>>({
    AccessRole: 'and',
    AddOn: 'and',
    Child: 'and',
    Class: 'and',
    ClassGroup: 'and',
    GroupRole: 'and',
    Parent: 'and',
    ProfileRole: 'and',
    UserStatus: 'and',
  });
  const [search, setSearch] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<PersonLabelType[]>([]);
  const [selectedPersonUuids, setSelectedPersonUuids] = useState<string[]>([]);
  const [currentPersonCount, setCurrentPersonCount] = useState<number>(0);
  const [maxPersonCount, setMaxPersonCount] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);

  async function handleSubmit() {
    setLoading(true);

    const courseTeachers = teachers.map((a) => a.uuid) ?? [];
    const teacherUuids = role === GroupRoles.Editor ? [...courseTeachers, ...selectedPersonUuids] : courseTeachers;

    const courseStudents = students.map((a) => a.uuid) ?? [];
    const studentUuids = role === GroupRoles.Viewer ? [...courseStudents, ...selectedPersonUuids] : courseStudents;

    const { error } = await bpUpdateGroup({
      uuid: courseUuid,
      update: { editors: teacherUuids, viewers: studentUuids },
    });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    setLoading(false);
    handleClose();
  }

  function handleLabelToggle(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      } else {
        return prev.filter((l) => !labelIsInLabels(label, [l]));
      }
    });
  }

  function handleLabelAdd(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (!labelIsInLabels(label, prev)) {
        return [...prev, label];
      }
      return prev;
    });
  }

  function handleLabelClose(label: PersonLabelType) {
    setSelectedLabels((prev) => {
      if (labelIsInLabels(label, prev)) {
        return prev.filter((l) => l !== label);
      }
      return prev;
    });
  }

  function handleCloseAllLabels() {
    setSelectedLabels([]);
  }

  function handleSearch(value: string) {
    setSearch(value);
  }

  function handleSourceFilter(sources: PersonLabelSource[]) {
    setActiveSources(sources);
  }

  function handleFilterModeChange(source: PersonLabelSource, mode: 'and' | 'or') {
    setFilterMode((prev) => ({
      ...prev,
      [source]: mode,
    }));
  }

  function handlePersonSelect(uuid: string) {
    setSelectedPersonUuids((prev) => {
      if (prev.includes(uuid)) {
        return prev.filter((personUuid) => personUuid !== uuid);
      }
      return [...prev, uuid];
    });
  }

  function handleSelectAll(uuids: string[]) {
    setSelectedPersonUuids((prev) => {
      const allSelected = uuids.every((u) => prev.includes(u));
      if (allSelected) {
        return prev.filter((id) => !uuids.includes(id));
      }
      const set = new Set(prev);
      uuids.forEach((u) => set.add(u));
      return Array.from(set);
    });
  }

  function handlePersonCountChanged(current: number, max: number) {
    setCurrentPersonCount(current);
    setMaxPersonCount(max);
  }

  function handleClose() {
    setSelectedLabels([]);
    setSelectedPersonUuids([]);
    onClose();
  }

  const headline =
    role === GroupRoles.Editor
      ? t('courses.addMemberType', { type: t('rolesInOrganization.teacher', { count: 2 }) })
      : t('courses.addMemberType', { type: t('rolesInOrganization.student', { count: 2 }) });

  const selectedNames: string[] = useMemo(() => {
    return selectedPersonUuids.map((uuid) => {
      return persons.find((p) => p.uuid === uuid)?.listName ?? '';
    });
  }, [selectedPersonUuids, persons]);

  return (
    <Modal
      title={headline}
      loading={loading}
      isOpen={isOpen}
      onRequestClose={() => handleClose()}
      className={styles['course-members-modal']}
    >
      <Grid className={styles.grid}>
        <GridRow>
          <PersonsLabelFilter
            selectedLabels={selectedLabels}
            showSourcesFilter
            availableSources={[
              'AccessRole',
              'AddOn',
              'Child',
              'Class',
              'ClassGroup',
              'Parent',
              'ProfileRole',
              'UserStatus',
            ]}
            activeSources={activeSources}
            filterMode={filterMode}
            showQuickSelection
            showSearch
            showCount
            currentCount={currentPersonCount}
            maxCount={maxPersonCount}
            onQuickSelection={(label) => handleLabelToggle(label)}
            onLabelClose={(label) => handleLabelClose(label)}
            onCloseAll={() => handleCloseAllLabels()}
            onSearch={(value) => handleSearch(value)}
            onSourceFilter={(sources) => handleSourceFilter(sources)}
            onFilterModeChange={handleFilterModeChange}
            data={data}
          />
        </GridRow>
        <GridRow>
          <div className={styles.scroll} ref={scrollRef}>
            <PersonsList
              persons={persons}
              selectedLabels={selectedLabels}
              filterMode={filterMode}
              search={search}
              activeSources={activeSources}
              selectedPersons={selectedPersonUuids}
              onSelectRow={(uuid) => handlePersonSelect(uuid)}
              onSelectAll={(uuids) => handleSelectAll(uuids)}
              onLabelClick={(label) => handleLabelAdd(label)}
              onCountChanged={(current, max) => handlePersonCountChanged(current, max)}
              data={data}
              scrollContainer={scrollRef}
            />
          </div>
        </GridRow>
      </Grid>

      <ModalBottomButtons
        closeButton={{ callback: () => handleClose() }}
        submitButton={{ callback: () => handleSubmit() }}
        additionalLeftComponent={
          selectedPersonUuids.length > 0 && (
            <div className={styles.info}>
              {t('courses.addMemberCount', { count: selectedPersonUuids.length })}
              <Tooltip content={selectedNames.join('\n')}>
                <InfoIcon className={styles.icon} />
              </Tooltip>
            </div>
          )
        }
      />
    </Modal>
  );
};
